import { Typography, styled } from "@mui/material"
import { usePermissions } from "../../../hooks/usePermissions"

import { dayjsUTC } from "../../../utils/dates"
import { getThirtyDaysFromNowDate } from "../../../utils/helpers/dateHelper"
import { extraColors } from "../../../utils/theme"
import Button from "../../Shared/Button"
import {
  useUpdateUserStatus,
  useUpdateUserTrial,
  type PharosUserTrial,
} from "../hooks/useManageAssetServiceUsers"

const BUTTON_WIDTH = "230px"

const UserDetails = styled("div")({
  display: "flex",
  flexDirection: "column",
})

const StatusRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  gap: 6,
  marginBottom: 6,
})

const StatusTag = styled("div")<{ background: string; border: string }>(
  ({ background, border, theme }) => ({
    ...theme.typography.small1Bold,
    backgroundColor: background,
    padding: "1px 8px",
    borderRadius: 12,
    border: `1px solid ${border}`,
    color: border,
    display: "flex",
    alignItems: "center",
    fontSize: 9,
  })
)

const DeactivateButton = styled(Button)({
  border: `2px solid ${extraColors.medium}`,
  background: extraColors.white,
  color: extraColors.medium,
  width: BUTTON_WIDTH,
  "&:hover": {
    border: `2px solid ${extraColors.medium}`,
    background: extraColors.light,
    color: extraColors.medium,
  },
})

const ActionRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifySelf: "end",
  justifyContent: "space-evenly",
  gap: 20,
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    marginLeft: 20,
  },
}))

const UserContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridAutoFlow: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: 12,
  padding: "12px 20px",
  borderRadius: 6,
  border: `2px solid rgba(0, 140, 159, .15)`,
  transition: "background 250ms ease",
  [theme.breakpoints.up("sm")]: {
    gridAutoFlow: "column",
    gap: 0,
    justifyContent: "space-between",
  },
}))

const UserEmail = styled(Typography)(({ theme }) => ({
  overflowWrap: "anywhere",
  maxWidth: BUTTON_WIDTH,
  color: extraColors.status.navyDark,
  [theme.breakpoints.up("sm")]: {
    maxWidth: "unset",
  },
}))

export const UserWithTrial: React.FC<{
  userTrial: PharosUserTrial
}> = ({ userTrial }) => {
  const { user } = userTrial

  return (
    <UserContainer>
      <UserDetails>
        <UserStatus userTrial={userTrial} />
        <UserEmail variant="body1Bold" sx={{ color: extraColors.white }}>
          {user?.primary_email_address}
        </UserEmail>
      </UserDetails>
      <UserActions userTrial={userTrial} />
    </UserContainer>
  )
}

const UserStatus: React.FC<{
  userTrial: PharosUserTrial
}> = ({ userTrial }) => {
  const { is_trial_user, user } = userTrial

  return (
    <StatusRow>
      <StatusTag
        background={
          user?.deleted_at ? extraColors.light : extraColors.status.greenLight
        }
        border={
          user?.deleted_at ? extraColors.medium : extraColors.status.greenDark
        }
      >
        {user?.deleted_at ? "Deactivated" : "Active"}
      </StatusTag>
      {is_trial_user && (
        <StatusTag
          background={extraColors.status.navyLight}
          border={extraColors.status.navyDark}
        >
          {getTrialPillText(userTrial)}
        </StatusTag>
      )}
    </StatusRow>
  )
}

const UserActions: React.FC<{
  userTrial: PharosUserTrial
}> = ({ userTrial }) => {
  const { mutate: toggleDeletedStatus } = useUpdateUserStatus()
  const { mutate: updateUserTrial } = useUpdateUserTrial()
  const permissions = usePermissions()
  const isPHCAdmin = !!permissions.phcAdminUser.organizations.users.write
  const { is_trial_user, user, trial_started_at } = userTrial

  const updateUserTrialStatus = () => {
    if (!user?.user_id) return
    const now = new Date()
    const thirtyDaysInFuture = getThirtyDaysFromNowDate(now)

    updateUserTrial({
      userTrial: {
        user: { user_id: user.user_id },
        is_trial_user: !is_trial_user,
        trial_started_at: !is_trial_user ? now.toISOString() : trial_started_at,
        trial_ended_at: !is_trial_user
          ? thirtyDaysInFuture.toISOString()
          : now.toISOString(),
      },
    })
  }

  return (
    <ActionRow>
      <Button
        variant="contained"
        sx={{ width: BUTTON_WIDTH }}
        disabled={!isPHCAdmin}
        onClick={updateUserTrialStatus}
      >
        {is_trial_user ? "End 30-Day Trial" : "Convert to 30-Day Trial"}
      </Button>
      <DeactivateButton
        disabled={!isPHCAdmin}
        variant="outlined"
        onClick={() => {
          toggleDeletedStatus({
            userId: user?.user_id,
            isActive: !!userTrial.user?.deleted_at,
          })
        }}
      >
        {user?.deleted_at ? "Reactivate User" : "Deactivate User"}
      </DeactivateButton>
    </ActionRow>
  )
}

function getTrialPillText(userTrial: PharosUserTrial) {
  const { trial_ended_at, trial_started_at } = userTrial
  const trialStartedAtDate = trial_started_at
    ? dayjsUTC(new Date(trial_started_at)).local().format("MM/DD/YYYY")
    : ""

  const trialStartedText = trial_started_at ? `: ${trialStartedAtDate}` : ""

  const trialEndedAtDate = trial_ended_at
    ? dayjsUTC(new Date(trial_ended_at)).local().format("MM/DD/YYYY")
    : ""

  const trialEndedAtText = trial_ended_at ? ` - ${trialEndedAtDate}` : ""

  return `Trial${trialStartedText}${trialEndedAtText}`
}
